import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template178: Aeropuertos SF - bloques separados (intro, sfo, oak, sjc, recomendacion, consejo)
const Template178 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;
	const contentColor = textColor || "#444";

	const getImageSrc = (id) =>
		page.images?.find((img) => img.id === id)?.src ?? "";
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const headerText = page.texts?.[0]?.content ?? "";
	const introText = page.texts?.[1]?.content ?? "";
	const sfo = sections[0] ?? {};
	const oak = sections[1] ?? {};
	const sjc = sections[2] ?? {};
	const recomendacion = page?.recomendacion ??
		page?.data?.recomendacion ??
		page?.data?.data?.recomendacion ?? { text: "" };
	const consejo =
		page?.consejo ?? page?.data?.consejo ?? page?.data?.data?.consejo ?? {};
	const tipBox =
		page?.tipBox ?? page?.data?.tipBox ?? page?.data?.data?.tipBox ?? {};
	const imageId = tipBox.imageId ?? "airport-sf";
	const airportImageSrc = getImageSrc(imageId);

	const box = {
		backgroundColor: "#fbf0cf",
		border: "2px solid #F2B705",
		borderRadius: "16px",
		padding: "16px",
	};
	const h3 = {
		fontFamily: "'Poppins', sans-serif",
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: 1,
		marginTop: 0,
		color: "#ca9904",
	};
	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};
	const ul = { paddingLeft: "18px", margin: 0 };

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100vh",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#F2B705",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{headerText}
			</Box>
			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "12px 16px", fontSize: 13, color: contentColor }}>
					<Box component="p" sx={p}>
						{introText}
					</Box>
				</Box>
			)}
			<Box
				data-block-id="text-2"
				sx={{
					padding: "0 16px 20px",
					display: "flex",
					flexDirection: "column",
					gap: 2,
					position: "relative",
				}}
			>
				{/* ——— SFO ——— */}
				{(sfo.title || sfo.items?.length) && (
					<Box data-block-id="text-3" sx={box}>
						{sfo.title && (
							<Box component="h3" sx={h3}>
								{sfo.title}
							</Box>
						)}
						{sfo.items?.length > 0 && (
							<Box component="ul" sx={ul}>
								{sfo.items.map((item, i) => (
									<li
										key={i}
										style={p}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label
															? `<strong>${item.label}</strong> `
															: "") + (item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				)}
				{/* ——— OAK ——— */}
				{(oak.title || oak.items?.length) && (
					<Box data-block-id="text-4" sx={box}>
						{oak.title && (
							<Box component="h3" sx={h3}>
								{oak.title}
							</Box>
						)}
						{oak.items?.length > 0 && (
							<Box component="ul" sx={ul}>
								{oak.items.map((item, i) => (
									<li
										key={i}
										style={p}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label
															? `<strong>${item.label}</strong> `
															: "") + (item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				)}
				{/* ——— SJC ——— */}
				{(sjc.title || sjc.items?.length) && (
					<Box data-block-id="text-5" sx={box}>
						{sjc.title && (
							<Box component="h3" sx={h3}>
								{sjc.title}
							</Box>
						)}
						{sjc.items?.length > 0 && (
							<Box component="ul" sx={ul}>
								{sjc.items.map((item, i) => (
									<li
										key={i}
										style={p}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label
															? `<strong>${item.label}</strong> `
															: "") + (item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				)}
				{/* ——— Recomendación + imagen ——— */}
				{recomendacion.text && (
					<Box
						data-block-id="text-6"
						sx={{
							backgroundColor: "#fff",
							border: "2px solid #f5c400",
							borderRadius: "12px",
							padding: "12px 14px",
							fontSize: 13,
							marginTop: 0,
						}}
						dangerouslySetInnerHTML={{ __html: recomendacion.text }}
					/>
				)}
				{airportImageSrc && (
					<Box
						component="img"
						src={airportImageSrc}
						alt=""
						sx={{
							width: isSmallScreen ? 90 : 100,
							position: "absolute",
							top: isSmallScreen ? 160 : 60,
							right: isSmallScreen ? -20 : -10,
						}}
					/>
				)}
				{/* ——— Consejo ——— */}
				{(consejo.title || consejo.body) && (
					<Box
						data-block-id="text-7"
						sx={{
							backgroundColor: "#fbf0cf",
							border: "2px solid #F2B705",
							borderRadius: "16px",
							padding: "16px",
							fontSize: 12,
							color: contentColor,
						}}
					>
						{consejo.title && (
							<Box
								component="h4"
								sx={{
									marginTop: 0,
									marginBottom: 1,
									fontSize: 13,
									color: "#ca9904",
									fontWeight: 700,
									textTransform: "uppercase",
								}}
							>
								{consejo.title}
							</Box>
						)}
						{consejo.body && (
							<Box
								component="p"
								sx={p}
								dangerouslySetInnerHTML={{ __html: consejo.body }}
							/>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template178.thumbnail = "/images/template-1.png";
export default Template178;
